import React, { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import "./BackNavigationGuard.css";

const GUARD_STATE_KEY = "__webBackGuardId";
const DEFAULT_GUARDED_ROUTES = ["/checkout/*", "/payment", "/account/*"];
const BACK_GUARD_ENABLED = !["0", "false", "off", "no", "disabled"].includes(
  String(process.env.REACT_APP_ENABLE_BACK_NAV_GUARD || "true")
    .trim()
    .toLowerCase()
);
const BACK_GUARD_ROUTES = String(process.env.REACT_APP_BACK_NAV_GUARD_ROUTES || "")
  .split(",")
  .map((entry) => entry.trim())
  .filter(Boolean);
const GUARDED_ROUTES = BACK_GUARD_ROUTES.length ? BACK_GUARD_ROUTES : DEFAULT_GUARDED_ROUTES;

function normalizePathname(pathname = "/") {
  if (!pathname) return "/";
  const next = pathname.trim();
  if (next === "/") return "/";
  return next.endsWith("/") ? next.slice(0, -1) : next;
}

function isGuardedPath(pathname = "/") {
  const normalizedPath = normalizePathname(pathname);
  return GUARDED_ROUTES.some((rule) => {
    const normalizedRule = normalizePathname(rule);
    if (!normalizedRule) return false;

    if (normalizedRule.endsWith("/*")) {
      const prefix = normalizedRule.slice(0, -1); // keeps trailing slash
      return normalizedPath.startsWith(prefix);
    }

    return normalizedPath === normalizedRule;
  });
}

function BackNavigationGuard() {
  const location = useLocation();
  const [isOpen, setIsOpen] = useState(false);
  const bypassPopCountRef = useRef(0);
  const guardIdRef = useRef("");
  const isCurrentPathGuardedRef = useRef(false);
  const popTriggeredRestoreRef = useRef(false);

  useEffect(() => {
    if (!BACK_GUARD_ENABLED) return;
    const isPathGuarded = isGuardedPath(location.pathname);
    isCurrentPathGuardedRef.current = isPathGuarded;

    if (!isPathGuarded) {
      setIsOpen(false);
      return;
    }

    const guardId = `${location.key}|${location.pathname}${location.search}${location.hash}`;
    guardIdRef.current = guardId;

    const currentState = window.history.state ?? {};
    if (currentState?.[GUARD_STATE_KEY] === guardId) return;

    window.history.pushState(
      {
        ...currentState,
        [GUARD_STATE_KEY]: guardId,
      },
      "",
      window.location.href
    );
  }, [location.hash, location.key, location.pathname, location.search]);

  useEffect(() => {
    if (!BACK_GUARD_ENABLED) return undefined;

    const onPopState = () => {
      if (!isCurrentPathGuardedRef.current) return;

      if (bypassPopCountRef.current > 0) {
        bypassPopCountRef.current -= 1;
        return;
      }

      setIsOpen(true);
      popTriggeredRestoreRef.current = false;

      const currentState = window.history.state ?? {};
      if (currentState?.[GUARD_STATE_KEY] !== guardIdRef.current) {
        popTriggeredRestoreRef.current = true;
        window.history.pushState(
          {
            ...currentState,
            [GUARD_STATE_KEY]: guardIdRef.current,
          },
          "",
          window.location.href
        );
      }
    };

    window.addEventListener("popstate", onPopState);
    return () => window.removeEventListener("popstate", onPopState);
  }, []);

  const handleCancel = () => {
    popTriggeredRestoreRef.current = false;
    setIsOpen(false);
  };

  const handleConfirmBack = () => {
    setIsOpen(false);
    if (popTriggeredRestoreRef.current) {
      popTriggeredRestoreRef.current = false;
      bypassPopCountRef.current = 1;
      window.history.go(-2);
      return;
    }

    bypassPopCountRef.current = 1;
    window.history.back();
  };

  if (!BACK_GUARD_ENABLED) return null;
  if (!isOpen) return null;

  return (
    <div className="back-guard__overlay" role="dialog" aria-modal="true" aria-labelledby="back-guard-title">
      <div className="back-guard__modal">
        <div className="back-guard__icon-wrap" aria-hidden="true">
          <span className="back-guard__icon">↩</span>
        </div>
        <h3 id="back-guard-title" className="back-guard__title">
          Do you want to go back?
        </h3>
        <p className="back-guard__description">
          You are about to leave this page. Tap yes only if you want to continue.
        </p>

        <div className="back-guard__actions">
          <button type="button" className="back-guard__btn back-guard__btn--ghost" onClick={handleCancel}>
            Cancel
          </button>
          <button type="button" className="back-guard__btn back-guard__btn--primary" onClick={handleConfirmBack}>
            Yes
          </button>
        </div>
      </div>
    </div>
  );
}

export default BackNavigationGuard;
